import type {RefObject} from 'react';
import {useCallback, useState} from 'react';
import type {GridApiPro} from '@mui/x-data-grid-pro';
import {DEFAULT_PAGINATION_MODEL, type PaginationModel} from '../types';
import {resetToFirstPage} from '../functions/resetToFirstPage';

/**
 * Local copy. Updated via onPaginationModelChange, never returned back to the grid.
 */
export const useDataTablePagination = (
  apiRef: RefObject<GridApiPro | null>,
  initialPaginationModel?: Partial<PaginationModel>,
  tableKey?: string,
) => {
  const [localPaginationModel, setLocalPaginationModel] =
    useState<PaginationModel>(() => ({
      ...DEFAULT_PAGINATION_MODEL,
      ...initialPaginationModel,
    }));

  const [currentTableKey, setCurrentTableKey] = useState(tableKey);
  if (currentTableKey !== tableKey) {
    setCurrentTableKey(tableKey);
    setLocalPaginationModel({
      ...DEFAULT_PAGINATION_MODEL,
      ...initialPaginationModel,
    });
  }

  const onPaginationModelChange = useCallback((model: PaginationModel) => {
    setLocalPaginationModel(model);
  }, []);

  const resetPagination = useCallback(() => {
    setLocalPaginationModel(prev =>
      prev.page === 0 ? prev : {...prev, page: 0},
    );
    resetToFirstPage(apiRef);
  }, [apiRef]);

  return {
    paginationModel: localPaginationModel,
    onPaginationModelChange,
    resetPagination,
  };
};
